import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MessageService } from '@corelabs/angular-messages';
import { TranslateService } from '@ngx-translate/core';
import { catchError, concatMap, delay, map, mergeMap, of, switchMap, tap } from 'rxjs';
import { finalizeLoader } from '@app/shared/components';
import { learningPortfolioActions } from '../actions/learning-portfolio.actions';
import { HttpService } from '../services/http.service';

@Injectable()
export class LearningPortfolioEffects {
    private readonly actions = inject(Actions);
    private readonly httpService = inject(HttpService);
    private readonly messageService = inject(MessageService);
    private readonly translateService = inject(TranslateService);

    getNotes$ = createEffect(() => {
        return this.actions.pipe(
            ofType(learningPortfolioActions.getNotes),
            switchMap(({ params }) => {
                return this.httpService.getNotes(params).pipe(
                    map((payload) => learningPortfolioActions.getNotesSuccess({ params, payload })),
                    catchError(() => of(learningPortfolioActions.getNotesError({ params }))),
                );
            }),
        );
    });

    getNote$ = createEffect(() => {
        return this.actions.pipe(
            ofType(learningPortfolioActions.getNote),
            switchMap(({ id }) => {
                return this.httpService.getNote(id).pipe(
                    map((payload) => learningPortfolioActions.getNoteSuccess({ payload })),
                    catchError(() => of(learningPortfolioActions.getNoteError({ id }))),
                );
            }),
        );
    });

    getCategories$ = createEffect(() => {
        return this.actions.pipe(
            ofType(learningPortfolioActions.getCategories),
            mergeMap(() => {
                return this.httpService.getCategories().pipe(
                    map((payload) => learningPortfolioActions.getCategoriesSuccess({ payload })),
                    catchError(() => of(learningPortfolioActions.getCategoriesError())),
                );
            }),
        );
    });

    createCategory$ = createEffect(() => {
        return this.actions.pipe(
            ofType(learningPortfolioActions.createCategory),
            concatMap(({ name }) => {
                return this.httpService.createCategory(name).pipe(
                    map((payload) => learningPortfolioActions.createCategorySuccess({ payload })),
                    catchError(() => of(learningPortfolioActions.createCategoryError())),
                );
            }),
        );
    });

    createNote$ = createEffect(() => {
        return this.actions.pipe(
            ofType(learningPortfolioActions.createNote),
            concatMap(({ value }) => {
                return this.httpService.createNote(value).pipe(
                    finalizeLoader('learning-portfolio-create'),
                    map((payload) => learningPortfolioActions.createNoteSuccess({ payload })),
                    catchError(() => of(learningPortfolioActions.createNoteError())),
                );
            }),
        );
    });

    updateNote$ = createEffect(() => {
        return this.actions.pipe(
            ofType(learningPortfolioActions.updateNote),
            concatMap(({ id, value }) => {
                return this.httpService.updateNote(id, value).pipe(
                    finalizeLoader('learning-portfolio-update'),
                    map((payload) => learningPortfolioActions.updateNoteSuccess({ payload })),
                    catchError(() => of(learningPortfolioActions.updateNoteError())),
                );
            }),
        );
    });

    uploadAttachments$ = createEffect(() => {
        return this.actions.pipe(
            ofType(learningPortfolioActions.uploadAttachments),
            mergeMap(({ uploadId, files }) => {
                return this.httpService.uploadAttachments(files).pipe(
                    map((payload) => learningPortfolioActions.uploadAttachmentsSuccess({ uploadId, payload })),
                    catchError(() => of(learningPortfolioActions.uploadAttachmentsError({ uploadId }))),
                );
            }),
        );
    });

    deleteNote$ = createEffect(() => {
        return this.actions.pipe(
            ofType(learningPortfolioActions.deleteNote),
            concatMap(({ id }) => {
                return this.httpService.deleteNote(id).pipe(
                    finalizeLoader('learning-portfolio-delete'),
                    map(() => learningPortfolioActions.deleteNoteSuccess({ id })),
                    catchError(() => of(learningPortfolioActions.deleteNoteError())),
                );
            }),
        );
    });

    createNoteSuccess$ = createEffect(
        () => {
            return this.actions.pipe(
                ofType(learningPortfolioActions.createNoteSuccess),
                tap(() => this.messageService.add(this.translateService.instant('toast.learning-portfolio.create-success'), 'success')),
            );
        },
        { dispatch: false },
    );

    updateNoteSuccess$ = createEffect(
        () => {
            return this.actions.pipe(
                ofType(learningPortfolioActions.updateNoteSuccess),
                tap(() => this.messageService.add(this.translateService.instant('toast.learning-portfolio.update-success'), 'success')),
            );
        },
        { dispatch: false },
    );

    deleteNoteSuccess$ = createEffect(
        () => {
            return this.actions.pipe(
                ofType(learningPortfolioActions.deleteNoteSuccess),
                tap(() => this.messageService.add(this.translateService.instant('toast.learning-portfolio.delete-success'), 'success')),
            );
        },
        { dispatch: false },
    );

    clearSaved$ = createEffect(() => {
        return this.actions.pipe(
            ofType(
                learningPortfolioActions.createNoteSuccess,
                learningPortfolioActions.createNoteError,
                learningPortfolioActions.updateNoteSuccess,
                learningPortfolioActions.updateNoteError,
            ),
            delay(0),
            map(() => learningPortfolioActions.clearSaved()),
        );
    });

    clearDeleted$ = createEffect(() => {
        return this.actions.pipe(
            ofType(learningPortfolioActions.deleteNoteSuccess, learningPortfolioActions.deleteNoteError),
            delay(0),
            map(() => learningPortfolioActions.clearDeleted()),
        );
    });

    error$ = createEffect(
        () => {
            return this.actions.pipe(
                ofType(
                    learningPortfolioActions.createNoteError,
                    learningPortfolioActions.updateNoteError,
                    learningPortfolioActions.deleteNoteError,
                    learningPortfolioActions.getNotesError,
                    learningPortfolioActions.getNoteError,
                    learningPortfolioActions.uploadAttachmentsError,
                    learningPortfolioActions.createCategoryError,
                ),
                tap(() => this.messageService.add(this.translateService.instant('toast.learning-portfolio.error'), 'danger')),
            );
        },
        { dispatch: false },
    );
}
